import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, AlertCircle, CheckCircle, Calculator } from 'lucide-react';
import TeamSelector from '../teams/TeamSelector';
import toast from 'react-hot-toast';

const StepResultForm = ({ eventId, stepNumber, onClose, onSubmit, isLoading = false }) => {
  const [formData, setFormData] = useState({
    localTeam: null,
    visitorTeam: null,
    localScore: '',
    visitorScore: '',
    drawOdds: '',
    maxNonDraw: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Detectar automáticamente si es empate
  const isDrawResult = formData.localScore !== '' && formData.visitorScore !== '' &&
    parseInt(formData.localScore) === parseInt(formData.visitorScore);

  // Validar formulario con debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const newErrors = {};

      if (!formData.localTeam) newErrors.localTeam = 'Selecciona el equipo local';
      if (!formData.visitorTeam) newErrors.visitorTeam = 'Selecciona el equipo visitante';
      if (formData.localTeam && formData.visitorTeam && formData.localTeam.id === formData.visitorTeam.id) {
        newErrors.visitorTeam = 'Los equipos deben ser diferentes';
      }
      if (formData.localScore === '') newErrors.localScore = 'Ingresa los goles del equipo local';
      if (formData.visitorScore === '') newErrors.visitorScore = 'Ingresa los goles del equipo visitante';
      if (!formData.drawOdds || parseFloat(formData.drawOdds) <= 1) {
        newErrors.drawOdds = 'La cuota de empate debe ser mayor a 1.00';
      }

      setErrors(newErrors);
    }, 300); // Debounce de 300ms

    return () => clearTimeout(timeoutId);
  }, [formData, isDrawResult]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (Object.keys(errors).length > 0) {
      toast.error('Por favor corrige los errores antes de continuar');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        localTeamId: formData.localTeam?.id || null,
        visitorTeamId: formData.visitorTeam?.id || null,
        localScore: formData.localScore !== '' ? parseInt(formData.localScore) : null,
        visitorScore: formData.visitorScore !== '' ? parseInt(formData.visitorScore) : null,
        result: isDrawResult ? 'draw' : 'no_draw',
        drawOdds: formData.drawOdds ? parseFloat(formData.drawOdds) : null,
        maxNonDraw: formData.maxNonDraw ? parseFloat(formData.maxNonDraw) : null,
        payoutFactor: isDrawResult && formData.drawOdds ? parseFloat(formData.drawOdds) : null,
        matchName: formData.localTeam && formData.visitorTeam ?
          `${formData.localTeam.name} vs ${formData.visitorTeam.name}` :
          `Partido Paso ${stepNumber}`
      };

      console.log('🚀 Datos preparados para enviar:', submitData);
      await onSubmit(submitData);
      toast.success(`Resultado registrado: ${isDrawResult ? 'EMPATE' : 'NO EMPATE'}`);
      onClose();

    } catch (error) {
      console.error('Error submitting step result:', error);
      toast.error(error.response?.data?.message || 'Error al registrar el resultado');
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculatePotentialPayout = () => {
    if (!formData.drawOdds || !isDrawResult) return null;
    const odds = parseFloat(formData.drawOdds);
    return odds > 1 ? `${odds.toFixed(2)}x` : null;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-white dark:bg-dark-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-600">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Registrar Resultado - Paso {stepNumber}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Ingresa los detalles del partido y el resultado final
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Equipos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Equipo Local *
              </label>
              <TeamSelector
                value={formData.localTeam}
                onChange={(team) => setFormData(prev => ({ ...prev, localTeam: team }))}
                placeholder="Buscar equipo local..."
                className={errors.localTeam ? 'border-red-300' : ''}
              />
              {errors.localTeam && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.localTeam}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Equipo Visitante *
              </label>
              <TeamSelector
                value={formData.visitorTeam}
                onChange={(team) => setFormData(prev => ({ ...prev, visitorTeam: team }))}
                placeholder="Buscar equipo visitante..."
                className={errors.visitorTeam ? 'border-red-300' : ''}
              />
              {errors.visitorTeam && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.visitorTeam}
                </p>
              )}
            </div>
          </div>

          {/* Marcadores */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Goles Local *
              </label>
              <input
                type="number"
                value={formData.localScore}
                onChange={(e) => setFormData(prev => ({ ...prev, localScore: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-700 dark:border-dark-600 dark:text-gray-100 ${
                  errors.localScore ? 'border-red-300' : 'border-gray-300 dark:border-dark-600'
                }`}
                min="0"
                step="1"
                disabled={isSubmitting}
              />
              {errors.localScore && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.localScore}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Goles Visitante *
              </label>
              <input
                type="number"
                value={formData.visitorScore}
                onChange={(e) => setFormData(prev => ({ ...prev, visitorScore: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-700 dark:border-dark-600 dark:text-gray-100 ${
                  errors.visitorScore ? 'border-red-300' : 'border-gray-300 dark:border-dark-600'
                }`}
                min="0"
                step="1"
                disabled={isSubmitting}
              />
              {errors.visitorScore && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.visitorScore}
                </p>
              )}
            </div>
          </div>

          {/* Cuotas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Cuota de Empate *
              </label>
              <input
                type="number"
                value={formData.drawOdds}
                onChange={(e) => setFormData(prev => ({ ...prev, drawOdds: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-700 dark:border-dark-600 dark:text-gray-100 ${
                  errors.drawOdds ? 'border-red-300' : 'border-gray-300 dark:border-dark-600'
                }`}
                min="1.01"
                step="0.01"
                placeholder="Ej: 3.25"
                disabled={isSubmitting}
              />
              {errors.drawOdds && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.drawOdds}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Máximo Sin Empate
                <span className="text-xs text-gray-500 ml-1">(opcional)</span>
              </label>
              <input
                type="number"
                value={formData.maxNonDraw}
                onChange={(e) => setFormData(prev => ({ ...prev, maxNonDraw: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-600 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-700 dark:text-gray-100"
                min="1.01"
                step="0.01"
                placeholder="Ej: 1.85"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Resultado detectado */}
          <div className={`p-4 rounded-lg border-2 border-dashed ${
            isDrawResult
              ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
              : formData.localScore !== '' && formData.visitorScore !== ''
                ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
                : 'bg-gray-50 dark:bg-dark-700 border-gray-300 dark:border-dark-600'
          }`}>
            <div className="flex items-center space-x-2">
              {isDrawResult ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : formData.localScore !== '' && formData.visitorScore !== '' ? (
                <X className="w-5 h-5 text-red-600" />
              ) : (
                <Calculator className="w-5 h-5 text-gray-400" />
              )}
              <div>
                <p className={`font-medium ${
                  isDrawResult
                    ? 'text-green-800 dark:text-green-300'
                    : formData.localScore !== '' && formData.visitorScore !== ''
                      ? 'text-red-800 dark:text-red-300'
                      : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {isDrawResult
                    ? '🟰 EMPATE DETECTADO'
                    : formData.localScore !== '' && formData.visitorScore !== ''
                      ? '❌ NO HAY EMPATE'
                      : '⏳ Esperando marcadores'
                  }
                </p>
                {isDrawResult && calculatePotentialPayout() && (
                  <p className="text-sm text-green-600 dark:text-green-400">
                    Multiplicador potencial: {calculatePotentialPayout()}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-dark-600">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting || Object.keys(errors).length > 0}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Registrando...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Registrar Resultado</span>
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default React.memo(StepResultForm);